/**
 * DamageEvent — unified damage broadcast shape.
 *
 * The server emits two parallel broadcasts (`playerDamaged` and
 * `npcDamaged`) whose shapes are nearly identical — only the target
 * identifier differs (`targetId` vs `npcId`). This file collapses both
 * into a single typed shape with a `targetKind` discriminator.
 *
 * DamageSyncSystem owns the conversion (PlayerDamagedEvent /
 * NpcDamagedEvent → DamageEvent) and re-emits the unified event on
 * EVENTS.DAMAGE_APPLIED. Consumers prefer that single channel over the
 * two raw broadcast aliases.
 *
 * 2026-05-12 — added as part of the Phase C.1 duplication-fix arc.
 * The drift hazard before this: three independent consumers
 * (CombatManager, DamagePresentationService, CombatLogService) each
 * parsed the raw broadcasts directly, casting to `any`. A field
 * rename on the server side would silently break all three; this
 * file is now the single point of conversion + typed contract.
 */

import type { PlayerDamagedEvent, NpcDamagedEvent, DamageDisplayFields } from '../types/NetworkMessages';

export type DamageTargetKind = 'player' | 'npc';

export interface DamageEvent extends DamageDisplayFields {
  /** Whether the target is a player session id or a server-side NPC id. */
  targetKind: DamageTargetKind;
  /** Stable target identifier (sid for players, npcId for NPCs). */
  targetId: string;
  /** Attacker session id. */
  attackerId: string;
  damage: number;
  critical: boolean;
  abilityId: string;
  damageSchool: string;
  /** ISO-8601 timestamp string. */
  timestamp: string;
  /** Plan 29 F1 defenses (players only, additive): reduced / absorbed / immune. */
  prevented?: number;
  absorbed?: number;
  immune?: true;
}

/** Optional trusted display evidence survives normalization without changing damage. */
export function damageDisplayFields(raw: DamageDisplayFields): DamageDisplayFields {
  return {
    ...(typeof raw.hitDamage === 'number' && Number.isFinite(raw.hitDamage) && raw.hitDamage >= 0 ? { hitDamage: raw.hitDamage } : {}),
    ...(typeof raw.x === 'number' && Number.isFinite(raw.x) && typeof raw.y === 'number' && Number.isFinite(raw.y)
      ? { x: raw.x, y: raw.y } : {}),
    ...(typeof raw.targetDefeated === 'boolean' ? { targetDefeated: raw.targetDefeated } : {}),
  };
}

export function fromPlayerDamaged(raw: PlayerDamagedEvent): DamageEvent {
  return {
    targetKind: 'player',
    targetId: raw.targetId,
    attackerId: raw.attackerId,
    damage: raw.damage,
    critical: raw.critical,
    abilityId: raw.abilityId,
    damageSchool: raw.damageSchool,
    timestamp: raw.timestamp,
    ...damageDisplayFields(raw),
    ...(raw.prevented ? { prevented: raw.prevented } : {}),
    ...(raw.absorbed ? { absorbed: raw.absorbed } : {}),
    ...(raw.immune ? { immune: true as const } : {}),
  };
}

export function fromNpcDamaged(raw: NpcDamagedEvent): DamageEvent {
  return {
    targetKind: 'npc',
    targetId: raw.npcId,
    attackerId: raw.attackerId,
    damage: raw.damage,
    critical: raw.critical,
    abilityId: raw.abilityId,
    damageSchool: raw.damageSchool,
    timestamp: raw.timestamp,
    ...damageDisplayFields(raw),
  };
}
